import { useState, useEffect } from 'react'
import { useOrg } from '../../components/OrgLayout'
import { useOrgAuditLogs } from '../../hooks/useOrgAuditLogs'
import { useOrgPermissions } from '../../hooks/useOrgPermissions'
import type { OrgAuditAction } from '../../types/database'
import {
  VStack,
  HStack,
  Text,
  Badge,
  Select,
  Skeleton,
  Banner,
  PageHeader,
  Animate,
  EmptyState,
  Button,
  Table,
  useToast
} from 'wss3-forge'
import {
  History20Regular,
  Person20Regular,
  Globe20Regular,
  Key20Regular,
  Apps20Regular,
  Settings20Regular,
  ArrowDown20Regular,
  ArrowDownload20Regular
} from '@fluentui/react-icons'

const ACTION_LABELS: Record<OrgAuditAction, string> = {
  member_invited: 'Member Invited',
  member_removed: 'Member Removed',
  member_role_changed: 'Role Changed',
  domain_added: 'Domain Added',
  domain_removed: 'Domain Removed',
  domain_verified: 'Domain Verified',
  sso_created: 'SSO Created',
  sso_updated: 'SSO Updated',
  sso_deleted: 'SSO Deleted',
  sso_toggled: 'SSO Toggled',
  settings_updated: 'Settings Updated',
  app_enabled: 'App Enabled',
  app_disabled: 'App Disabled'
}

const ACTION_OPTIONS = [
  { value: '', label: 'All Actions' },
  { value: 'member_invited', label: 'Member Invited' },
  { value: 'member_removed', label: 'Member Removed' },
  { value: 'member_role_changed', label: 'Role Changed' },
  { value: 'domain_added', label: 'Domain Added' },
  { value: 'domain_verified', label: 'Domain Verified' },
  { value: 'sso_created', label: 'SSO Created' },
  { value: 'sso_updated', label: 'SSO Updated' },
  { value: 'app_enabled', label: 'App Enabled' },
  { value: 'app_disabled', label: 'App Disabled' },
  { value: 'settings_updated', label: 'Settings Updated' }
]

function getActionIcon(action: OrgAuditAction) {
  if (action.startsWith('member_')) return <Person20Regular />
  if (action.startsWith('domain_')) return <Globe20Regular />
  if (action.startsWith('sso_')) return <Key20Regular />
  if (action.startsWith('app_')) return <Apps20Regular />
  return <Settings20Regular />
}

function getActionColor(action: OrgAuditAction): string {
  if (action.includes('removed') || action.includes('deleted') || action.includes('disabled')) {
    return 'var(--status-error)'
  }
  if (action.includes('added') || action.includes('created') || action.includes('enabled') || action.includes('verified')) {
    return 'var(--status-success)'
  }
  return 'var(--text-muted)'
}

export function OrgAuditLogs() {
  const { organization } = useOrg()
  const { logs, loading, error, totalCount, hasMore, filters, loadLogs, loadMore, setFilters } = useOrgAuditLogs()
  const { loadPermissions, canViewAudit } = useOrgPermissions()
  const toast = useToast()
  const [selectedIds, setSelectedIds] = useState<string[]>([])

  useEffect(() => {
    loadLogs(organization.id)
    loadPermissions(organization.id)
  }, [organization.id, loadLogs, loadPermissions])

  useEffect(() => {
    if (organization.id) {
      loadLogs(organization.id, true)
    }
  }, [filters, organization.id, loadLogs])

  const handleActionFilter = (value: string) => {
    setFilters({
      ...filters,
      action: value ? (value as OrgAuditAction) : null
    })
  }

  const handleExportCSV = () => {
    const logsToExport = selectedIds.length > 0
      ? logs.filter(log => selectedIds.includes(log.id))
      : logs

    if (logsToExport.length === 0) {
      toast.error('No logs to export')
      return
    }

    const headers = ['Date', 'User', 'Action', 'Target', 'Old Value', 'New Value']
    const rows = logsToExport.map(log => [
      new Date(log.created_at).toLocaleString(),
      log.actor_email || 'System',
      ACTION_LABELS[log.action as OrgAuditAction] || log.action,
      log.target_label || '',
      log.old_value ? JSON.stringify(log.old_value) : '',
      log.new_value ? JSON.stringify(log.new_value) : ''
    ])

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    ].join('\n')

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `audit-logs-${organization.slug}-${new Date().toISOString().split('T')[0]}.csv`
    link.click()
    URL.revokeObjectURL(url)

    toast.success(`Exported ${logsToExport.length} logs`)
  }

  if (!canViewAudit && !loading) {
    return (
      <Banner variant="error" title="Access Denied">
        You don't have permission to view audit logs.
      </Banner>
    )
  }

  if (loading && logs.length === 0) {
    return (
      <VStack gap="lg">
        <Skeleton width={200} height={32} />
        <Skeleton width="100%" height={400} />
      </VStack>
    )
  }

  return (
    <VStack gap="lg">
      <Animate type="fadeIn">
        <PageHeader
          title="Audit Logs"
          subtitle={`${totalCount} events recorded.`}
        />
      </Animate>

      {error && (
        <Banner variant="error" title="Error">
          {error}
        </Banner>
      )}

      {logs.length === 0 ? (
        <Animate type="fadeIn" delay={100}>
          <EmptyState
            icon={<History20Regular />}
            title="No audit logs"
            description="No activity has been recorded yet."
          />
        </Animate>
      ) : (
        <Animate type="slideInUp" delay={50}>
          <Table
            title="Audit Logs"
            columns={[
                {
                  key: 'created_at',
                  header: 'Date',
                  width: '180px',
                  render: (_, row) => (
                    <Text size="sm">
                      {new Date(row.created_at).toLocaleString()}
                    </Text>
                  )
                },
                {
                  key: 'actor_email',
                  header: 'User',
                  render: (_, row) => (
                    <Text size="sm" style={{ fontFamily: 'monospace' }}>
                      {row.actor_email || 'System'}
                    </Text>
                  )
                },
                {
                  key: 'action',
                  header: 'Action',
                  render: (_, row) => (
                    <HStack gap="sm" style={{ alignItems: 'center' }}>
                      <span style={{ color: getActionColor(row.action) }}>
                        {getActionIcon(row.action)}
                      </span>
                      <Badge
                        size="sm"
                        variant={
                          row.action.includes('removed') || row.action.includes('deleted') || row.action.includes('disabled')
                            ? 'error'
                            : row.action.includes('added') || row.action.includes('created') || row.action.includes('enabled')
                              ? 'success'
                              : 'default'
                        }
                      >
                        {ACTION_LABELS[row.action as OrgAuditAction] || row.action}
                      </Badge>
                    </HStack>
                  )
                },
                {
                  key: 'target_label',
                  header: 'Target',
                  render: (_, row) => (
                    <Text size="sm" color="muted">
                      {row.target_label || '-'}
                    </Text>
                  )
                },
                {
                  key: 'details',
                  header: 'Details',
                  render: (_, row) => {
                    if (row.old_value && row.new_value) {
                      const oldVal = JSON.stringify(row.old_value)
                      const newVal = JSON.stringify(row.new_value)
                      if (oldVal !== newVal) {
                        return (
                          <Text size="sm" color="muted">
                            {Object.keys(row.old_value as object).map(key => (
                              <span key={key}>
                                {key}: {String((row.old_value as Record<string, unknown>)[key])} → {String((row.new_value as Record<string, unknown>)[key])}
                              </span>
                            ))}
                          </Text>
                        )
                      }
                    }
                    return <Text size="sm" color="muted">-</Text>
                  }
                }
              ]}
              data={logs}
              selectable
              selectedKeys={selectedIds}
              onSelectionChange={setSelectedIds}
              globalActions={
                <HStack gap="sm">
                  <div style={{ minWidth: 180 }}>
                    <Select
                      value={filters.action || ''}
                      onChange={handleActionFilter}
                      options={ACTION_OPTIONS}
                    />
                  </div>
                  <Button
                    variant="secondary"
                    icon={<ArrowDownload20Regular />}
                    onClick={handleExportCSV}
                  >
                    Export CSV{selectedIds.length > 0 ? ` (${selectedIds.length})` : ''}
                  </Button>
                </HStack>
              }
          />
        </Animate>
      )}

      {hasMore && (
        <Animate type="fadeIn" delay={100}>
          <HStack style={{ justifyContent: 'center' }}>
            <Button
              variant="ghost"
              onClick={loadMore}
              loading={loading}
              icon={<ArrowDown20Regular />}
            >
              Load More
            </Button>
          </HStack>
        </Animate>
      )}
    </VStack>
  )
}
